/**
 * BoardProvider — the per-board runtime substrate. Owns the state that couples a
 * board to its chrome (the render root, the canvas command handle, the reported
 * viewport / breadcrumb, the smart-view operator) and hands it down through the
 * two contexts in `BoardContext`. Renders only `{children}`, so a board and the
 * chrome you compose around it are pure consumers of one substrate.
 *
 * Two correctness invariants make "a focal change re-renders chrome, not the
 * board" hold — both are load-bearing:
 *  1. This provider renders grandparent-supplied `{children}`; when its viewport
 *     state updates, those child ELEMENTS are stable references (created by the
 *     composer, e.g. InfiniteBoard, which does NOT re-render) so React doesn't
 *     re-invoke them — only the context consumers that read the CHANGED context
 *     re-render.
 *  2. The STABLE `BoardCommands` value is `useMemo`'d over just
 *     [root, operatorEnabled, operatorInteractive]; the closures it carries are
 *     ref-stable, so its identity does NOT change on a viewport state write — and
 *     `BoardSurface` (which reads it) is not re-rendered per focal frame.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { asSource, walkFocal, type FocalStep, type SpaceLike, type Viewport } from "@archlang/scene/board";
import type { BoardCanvasHandle } from "./BoardCanvas";
import {
  BoardCommandsContext,
  BoardFocalContext,
  type BoardCommands,
  type BoardCommandSet,
  type BoardFocal,
} from "./BoardContext";
import { useSmartViewHotkey } from "./useSmartViewHotkey";
import { hostStorage } from "../host/hostStorage.js";

const OPERATOR_PREF_KEY = "archlang.smartView.v1";

function readOperatorPref(defaultOn: boolean): boolean {
  const stored = hostStorage.getItem(OPERATOR_PREF_KEY);
  return stored === null ? defaultOn : stored === "1";
}
function writeOperatorPref(on: boolean): void {
  hostStorage.setItem(OPERATOR_PREF_KEY, on ? "1" : "0");
}

export interface BoardProviderProps {
  readonly root: SpaceLike;
  /**
   * Make the camera operator ("smart view") available: chrome shows the
   * follow-edits toggle and the user may turn it on. The board owns the on/off
   * state (persisted). Off (no toggle) for read-only embeds.
   */
  readonly operator?: boolean;
  /** Force smart-view permanently on with NO toggle (a showcase, e.g. the site
   *  demo). Ignores the persisted pref and hides the toggle. Implies operator. */
  readonly operatorForced?: boolean;
  readonly initialViewport?: Viewport;
  readonly onViewportChange?: (vp: Viewport) => void;
  readonly children: ReactNode;
}

export function BoardProvider({
  root,
  operator = false,
  operatorForced = false,
  initialViewport,
  onViewportChange,
  children,
}: BoardProviderProps) {
  const canvasRef = useRef<BoardCanvasHandle | null>(null);
  const registerCanvas = useCallback((h: BoardCanvasHandle | null) => {
    canvasRef.current = h;
  }, []);

  // Per-frame camera lives in a ref; React state carries only what chrome renders
  // from — the FOCAL path (breadcrumb). Same focalPath reference = a pan/zoom-only
  // frame → bail without a state write so chrome doesn't ride the gesture.
  const lastVpRef = useRef<Viewport | null>(initialViewport ?? null);
  const [viewport, setViewport] = useState<Viewport | null>(initialViewport ?? null);
  const reportViewport = useCallback(
    (vp: Viewport) => {
      lastVpRef.current = vp;
      onViewportChange?.(vp);
      setViewport((prev) => (prev && prev.focalPath === vp.focalPath ? prev : vp));
    },
    [onViewportChange],
  );
  const getViewport = useCallback(
    (): Viewport => canvasRef.current?.getViewport() ?? lastVpRef.current ?? ({} as Viewport),
    [],
  );

  // Smart-view on/off — board-owned + persisted, survives reload / workspace swap.
  const [smartView, setSmartView] = useState(false);
  // Enable AFTER mount (a tick later), never synchronously on first render — that
  // let the camera operator's effects run before the board/scene existed. Forced
  // resolves to "on"; otherwise honor the persisted pref (ON by default).
  useEffect(() => {
    if (operatorForced) setSmartView(true);
    else if (operator) setSmartView(readOperatorPref(true));
  }, [operator, operatorForced]);
  const operatorEnabled = (operator || operatorForced) && smartView;
  const showToggle = operator && !operatorForced;
  const toggleSmartView = useCallback((): void => {
    setSmartView((on) => {
      const next = !on;
      writeOperatorPref(next);
      return next;
    });
  }, []);
  // ⌃⌥F toggles smart-view from anywhere (incl. editor focus) — bound only when
  // the toggle is available (not forced, operator on).
  useSmartViewHotkey(showToggle, toggleSmartView);

  // Stable closures over the ref — identity fixed for the provider's life.
  const commands = useMemo<BoardCommandSet>(
    () => ({
      flyTo: (id) => canvasRef.current?.flyTo(id),
      enterSpace: (id) => canvasRef.current?.enterSpace(id),
      reframe: () => canvasRef.current?.reframe(),
      framePair: (a, b) => canvasRef.current?.framePair(a, b),
      restoreViewport: (vp) => canvasRef.current?.restoreViewport(vp),
      setViewportImmediate: (vp) => canvasRef.current?.setViewportImmediate(vp),
      popOut: () => canvasRef.current?.popOut(),
      resetView: () => canvasRef.current?.resetView(),
      navigateTo: (path) => canvasRef.current?.navigateTo(path),
      exportFocalAsPng: (opts) =>
        canvasRef.current?.exportFocalAsPng(opts) ?? Promise.reject(new Error("board not mounted")),
      exportFocalAsSvg: (opts) =>
        canvasRef.current?.exportFocalAsSvg(opts) ?? Promise.reject(new Error("board not mounted")),
      copyFocalToClipboard: (opts) =>
        canvasRef.current?.copyFocalToClipboard(opts) ?? Promise.reject(new Error("board not mounted")),
    }),
    [],
  );

  // STABLE context value — identity changes ONLY with root / operator flags, so a
  // viewport state write does not re-render `BoardSurface`. (See invariant 2.)
  const commandsValue = useMemo<BoardCommands>(
    () => ({ root, reportViewport, registerCanvas, getViewport, commands, operatorEnabled, operatorInteractive: showToggle }),
    [root, reportViewport, registerCanvas, getViewport, commands, operatorEnabled, showToggle],
  );

  // VOLATILE context value — chrome only.
  const steps = useMemo<readonly FocalStep[]>(
    () => (viewport ? walkFocal(root, viewport.focalPath) : [{ space: asSource(root).at([]) }]),
    [root, viewport],
  );
  const focalValue = useMemo<BoardFocal>(
    () => ({ viewport, steps, operator: { showToggle, on: smartView, toggle: toggleSmartView } }),
    [viewport, steps, showToggle, smartView, toggleSmartView],
  );

  return (
    <BoardCommandsContext.Provider value={commandsValue}>
      <BoardFocalContext.Provider value={focalValue}>{children}</BoardFocalContext.Provider>
    </BoardCommandsContext.Provider>
  );
}
